/**
 * festival_catalog.ts — THE SEASONAL FESTIVALS, the pure half (2026-09-24). MIRRORED byte-identical at
 * client/src/lib/festival_catalog.ts (verify:world-wave diffs them).
 *
 * The world's year is one real day: four seasons of 6 real hours each, aligned to UTC (lib/calendar_ops.ts —
 * 00–06 the Sowing, 06–12 High Sun, 12–18 the Harvest, 18–24 the Frost). Each season has its festival, and it
 * runs in the season's SECOND hour at every city, capital and town square — the same hour on every clock, so a
 * player can plan to be there (research §4.25: rituals aligned to the calendar; Stardew's fixed festival days).
 * The window is arithmetic, never a table: every client and the server agree to the microsecond.
 */

export type FestivalSeason = 'sowing' | 'highsun' | 'harvest' | 'frost';
export type ContestKind = 'donate' | 'fish' | 'produce' | 'fireworks';

export interface FestivalDef {
  key: string;
  season: FestivalSeason;
  name: string;
  /** One line on what the festival is. */
  blurb: string;
  contest: ContestKind;
  contestLabel: string;
  /** What the keepsake is (the collection). */
  keepsake: string;
}

export const FESTIVAL_SEASONS: FestivalSeason[] = ['sowing', 'highsun', 'harvest', 'frost'];

export const FESTIVALS: Record<FestivalSeason, FestivalDef> = {
  sowing: {
    key: 'melt_feast', season: 'sowing', name: 'the Melt Feast',
    blurb: 'the thaw is shared — every larder gives a little to carry the town through the hungry gap',
    contest: 'donate', contestLabel: 'Most grain or produce given to the town', keepsake: 'a thaw-bread token',
  },
  highsun: {
    key: 'longlight_fair', season: 'highsun', name: 'the Longlight Fair',
    blurb: 'the longest days — lines in the water, and the biggest fish takes the ribbon',
    contest: 'fish', contestLabel: 'The longest fish you landed yourself', keepsake: 'a sun-ribbon',
  },
  harvest: {
    key: 'harvest_home', season: 'harvest', name: 'Harvest Home',
    blurb: 'the last sheaf comes in, and the finest of the fields is judged on the long table',
    contest: 'produce', contestLabel: 'The finest produce (its quality)', keepsake: 'a corn-knot',
  },
  frost: {
    key: 'lantern_night', season: 'frost', name: 'Lantern Night',
    blurb: 'the longest night is lit from the ground up — every rocket is a lantern for the year',
    contest: 'fireworks', contestLabel: 'The most fireworks launched at the festival', keepsake: 'a paper lantern',
  },
};

/** A season is 6 real hours; its festival opens one hour in and runs one hour. */
export const SEASON_US = 6n * 3_600_000_000n;
export const FESTIVAL_OPEN_AFTER_US = 3_600_000_000n;
export const FESTIVAL_LEN_US = 3_600_000_000n;
/** The festival stands in these tiers (a hamlet has no square to hold one). */
export const FESTIVAL_TIERS = ['capital', 'city', 'town'] as const;
/** Within this of the festival's centre you are AT it (attendance, contests). */
export const FESTIVAL_ATTEND_M = 60;
/** A presence ping credits at most this much time (the service posts' duty law). */
export const FESTIVAL_PING_MAX_S = 40; // the duty law's cap (a post's ping credits ≤ 40 s; this said 45)
/** Stay this long and the keepsake is yours. */
export const FESTIVAL_KEEPSAKE_S = 300;
/** The festival's lift to the Social Pulse when the keepsake is given (never a penalty). */
export const KEEPSAKE_FUN = 10;
export const KEEPSAKE_SOCIAL = 6;
/** Lantern Night counts a rocket launched within this of the festival. */
export const FESTIVAL_FIREWORK_M = 80;
/** The judged places pay (credits, from the platform — a faucet bounded by festivals with players in them). */
export const FESTIVAL_PRIZES = [100, 60, 30];
/** A contest is judged only with at least this many entrants (2026-09-28): a lone entry at an empty festival won 100 ⓒ. */
export const FESTIVAL_MIN_ENTRANTS = 2;

export interface FestivalWindow {
  /** The absolute season epoch (increments once per season). */
  epoch: number;
  season: FestivalSeason;
  def: FestivalDef;
  startUs: bigint;
  endUs: bigint;
}

/** The festival of the season a timestamp falls in (its window may be ahead, running or behind). */
export function festivalWindowAt(micros: bigint): FestivalWindow {
  const epochBig = micros / SEASON_US;
  const season = FESTIVAL_SEASONS[Number(epochBig % 4n)];
  const startUs = epochBig * SEASON_US + FESTIVAL_OPEN_AFTER_US;
  return { epoch: Number(epochBig), season, def: FESTIVALS[season], startUs, endUs: startUs + FESTIVAL_LEN_US };
}

/** Is the festival of this season running at `micros`? */
export function festivalRunning(micros: bigint): boolean {
  const w = festivalWindowAt(micros);
  return micros >= w.startUs && micros < w.endUs;
}

/** The next festival that has not yet ended (this season's if it is still ahead or running, else the next season's). */
export function nextFestival(micros: bigint): FestivalWindow {
  const w = festivalWindowAt(micros);
  if (micros < w.endUs) return w;
  return festivalWindowAt((BigInt(w.epoch) + 1n) * SEASON_US);
}

/** The key a festival's row carries: `${epoch}:${settlementId}`. */
export function festivalKey(epoch: number, settlementId: bigint): string {
  return `${epoch}:${settlementId}`;
}

/** A produce stack's quality for the Harvest Home: the mean of its stats (1..1000). */
export function produceQuality(stats: Record<string, number>): number {
  const vals = Object.values(stats).filter((v) => typeof v === 'number' && Number.isFinite(v));
  if (vals.length === 0) return 0;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}
